import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';
import { 
    IconFileUp, IconZap, IconCheck, IconAlertTriangle, IconOps 
} from '../components/Icons';
import toast from 'react-hot-toast';

interface ExtractedData {
    doc_type: string;
    summary: string;
    sentiment: string;
    entities: Record<string, string>;
    key_data: { label: string; value: string }[];
}

interface DocumentIntelligenceViewProps {
    onBookTrade?: (ticket: any) => void;
}

const DocumentIntelligenceView: React.FC<DocumentIntelligenceViewProps> = ({ onBookTrade }) => {
    const { currentUser } = useAuth();
    const [file, setFile] = useState<File | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [result, setResult] = useState<ExtractedData | null>(null);

    // 1. Handle File Selection
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setResult(null); // Reset previous results
        }
    };

    // 2. Upload and Analyze
    const handleAnalyze = async () => {
        if (!file || !currentUser) return;
        
        setIsAnalyzing(true);
        const toastId = toast.loading("Uploading & Extracting Data...");

        try {
            const token = await currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch(`${API_BASE_URL}/doc/analyze`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }, // Do NOT set Content-Type for FormData
                body: formData
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Extraction failed");
            }

            const data = await res.json();
            setResult(data);
            toast.success("Data Extracted Successfully", { id: toastId });

        } catch (e: any) {
            console.error(e);
            toast.error(e.message, { id: toastId });
        } finally {
            setIsAnalyzing(false);
        }
    };

    // 3. Action: Book Trade from Document
    const handleAction = () => {
        if (!result || !onBookTrade) return;

        // Try to map extracted entities to a trade ticket
        const ticker = result.entities.ticker || "UNKNOWN";
        const price = parseFloat(result.entities.price || result.entities.amount || "0");
        const qty = parseFloat(result.entities.quantity || "0");
        
        if (price === 0) {
            toast.error("Could not extract valid trade details.");
            return;
        }

        const ticket = {
            product: `OTC ${ticker} (From Doc)`,
            ticker: ticker,
            side: "BUY", // Default
            price: price,
            quantity: qty > 0 ? qty : 100,
            status: "Pending",
            counterparty: "Document Extract"
        };
        
        onBookTrade(ticket);
        toast.success("Trade sent to Ops Blotter");
    };

    const getSentimentColor = (s: string) => {
        if (!s) return 'bg-slate-100 text-slate-600';
        const lower = s.toLowerCase();
        if (lower.includes('pos')) return 'bg-emerald-100 text-emerald-700';
        if (lower.includes('neg')) return 'bg-red-100 text-red-700';
        return 'bg-amber-100 text-amber-700';
    };

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden font-sans">
            
            {/* LEFT: UPLOAD ZONE */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">
                <div className="mb-8">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconFileUp className="text-purple-600" /> Doc Intelligence
                    </h2>
                    <p className="text-xs text-slate-500">PDF Data Extraction & Structuring</p>
                </div>

                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50 hover:bg-white hover:border-purple-400 transition-colors">
                    <input 
                        type="file" 
                        accept="application/pdf"
                        onChange={handleFileChange}
                        className="hidden" 
                        id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                        <IconFileUp size={48} className="text-slate-300 mb-4" />
                        <span className="text-sm font-bold text-slate-600">
                            {file ? file.name : "Click to Upload PDF"}
                        </span>
                        <span className="text-xs text-slate-400 mt-2">Earnings Reports, Invoices, Trade Confirms</span>
                    </label>
                </div>

                {file && (
                    <button 
                        onClick={handleAnalyze}
                        disabled={isAnalyzing}
                        className="mt-6 w-full bg-slate-900 text-white py-3 rounded-lg font-bold text-sm shadow-md hover:bg-black transition-all flex justify-center items-center gap-2 disabled:opacity-50"
                    >
                        {isAnalyzing ? (
                            <>Analyzing...</>
                        ) : (
                            <><IconZap size={16} className="text-yellow-400" /> Extract Data</>
                        )}
                    </button>
                )}

                <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 leading-relaxed">
                    <strong>How it works:</strong><br/>
                    The engine OCRs the document, uses an LLM to identify key financial entities, and returns structured JSON ready for trading or risk systems.
                </div>
            </div>

            {/* RIGHT: RESULTS */}
            <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
                {result ? (
                    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4">
                        
                        {/* HEADER CARD */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Document Type</div>
                                    <div className="text-2xl font-bold text-slate-800">{result.doc_type}</div>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getSentimentColor(result.sentiment)}`}>
                                    {result.sentiment} Sentiment
                                </span>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-3 rounded border border-slate-100">
                                {result.summary}
                            </p>
                        </div>

                        {/* KEY DATA GRID */}
                        <div className="grid grid-cols-2 gap-6">
                            {/* Key Values Table */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                    <IconZap size={16} className="text-amber-500" /> Extracted Metrics
                                </h3>
                                <div className="space-y-3">
                                    {result.key_data.map((item, idx) => (
                                        <div key={idx} className="flex justify-between text-sm border-b border-slate-50 pb-2">
                                            <span className="text-slate-500">{item.label}</span>
                                            <span className="font-mono font-bold text-slate-800">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Detected Entities / Action */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col">
                                <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                    <IconOps size={16} className="text-blue-500" /> Actionable Data
                                </h3>
                                <div className="flex-1">
                                    {Object.entries(result.entities).map(([key, val]) => (
                                        <div key={key} className="flex justify-between text-sm mb-2">
                                            <span className="text-slate-400 capitalize">{key}:</span>
                                            <span className="font-medium text-slate-800">{String(val)}</span>
                                        </div>
                                    ))}
                                </div>
                                
                                {result.entities.ticker && (
                                    <button 
                                        onClick={handleAction}
                                        className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded font-bold text-xs shadow-sm transition-colors flex justify-center items-center gap-2"
                                    >
                                        <IconOps size={14} /> Book Trade Ticket
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* JSON VIEW (For Nerds) */}
                        <div className="mt-8">
                             <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Raw JSON Output</h4>
                             <pre className="bg-slate-900 text-slate-300 p-4 rounded-lg text-xs overflow-x-auto font-mono">
                                 {JSON.stringify(result, null, 2)}
                             </pre>
                        </div>

                    </div>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center text-slate-400">
                        <IconFileUp size={64} className="opacity-20 mb-4" />
                        <p>Upload a document to extract insights.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentIntelligenceView;